import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import type { Address } from "@/types/users";
import { View, Button, RadioGroup } from "@/components/ui";
import { MapPinSimpleArea } from "@/components/shared/icons";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Typography } from "../../typography";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { Divider } from "../../divider";
import { RadioDotItem } from "@/components/shared/radio-dot-item";
import { getAddressDisplay } from "@/containers/booking-info/booking-info-form";

export const SelectAddressModal = ({
  open,
  addresses,
  selectedAddress,
  setOpen,
  onAddAddress,
  setSelectedAddress,
}: {
  open: boolean;
  addresses: Address[];
  selectedAddress: Address | null;
  setOpen(open: boolean): void;
  onAddAddress(): void;
  setSelectedAddress(address: Address): void;
}) => {
  const [currentSelectedAddress, setCurrentSelectedAddress] =
    useState(selectedAddress);

  useEffect(() => {
    setCurrentSelectedAddress(selectedAddress);
  }, [open, selectedAddress]);
  const { isDesktop } = useWindowDimensions();

  return (
    <BottomDrawerModal open={open} onOpenChange={setOpen}>
      <View className="flex flex-row items-center justify-between">
        <View className="flex flex-row items-center gap-3">
          <MapPinSimpleArea className="text-primary" size={24} weight="fill" />
          <Typography variant="label-md">Select Service Address</Typography>
        </View>
        {!isDesktop && (
          <Button
            variant="secondary"
            color="CTA2"
            rounded="full"
            onClick={onAddAddress}
            disabled={addresses.length >= 4}
            size="sm"
            iconName="plus"
            iconColor="CTA2"
            iconSize="sm"
            children="Add"
          />
        )}
      </View>

      <Divider className="my-2" />

      <RadioGroup
        value={currentSelectedAddress?.id}
        onValueChange={(id) => {
          const currentAddress = addresses.find(
            (addr) => addr.id === id,
          ) as Address;
          setCurrentSelectedAddress(currentAddress);
        }}
      >
        {addresses.map((addr) => {
          const addressDisplay = getAddressDisplay(addr);
          return (
            <View
              key={addr.postalCode}
              onClick={() => setCurrentSelectedAddress(addr)}
              className={cn(
                "flex cursor-pointer flex-row items-start gap-2 rounded-lg border p-3",
                currentSelectedAddress?.id === addr.id
                  ? "border-brand-primary-border"
                  : "border-secondary-border",
              )}
            >
              <View className="mt-1">
                <RadioDotItem
                  size={18}
                  checked={currentSelectedAddress?.id === addr.id}
                  onClick={() => setCurrentSelectedAddress(addr)}
                />
              </View>
              <View className="flex flex-1 flex-col gap-1">
                <Typography variant="label-sm">
                  {addressDisplay.label || addr.postalCode}
                </Typography>

                <Typography variant="body-sm" color="foreground-intermediate">
                  {addressDisplay.detailWithPostalCode}
                </Typography>
              </View>
            </View>
          );
        })}
      </RadioGroup>
      {isDesktop ? (
        <>
          <Divider className="my-2" />
          <View className="flex flex-row gap-2">
            <Button
              variant="secondary"
              color="CTA2"
              rounded="full"
              className="flex-1"
              onClick={onAddAddress}
              disabled={addresses.length >= 4}
              iconName="plus"
              iconColor="CTA2"
            >
              Add
            </Button>
            <Button
              className="flex-1 rounded-full"
              variant="primary"
              color="CTA"
              onClick={() => {
                setSelectedAddress(currentSelectedAddress as Address);
                setOpen(false);
              }}
            >
              Save
            </Button>
          </View>
        </>
      ) : (
        <Button
          variant="primary"
          color="CTA"
          onClick={() => {
            setSelectedAddress(currentSelectedAddress as Address);
            setOpen(false);
          }}
          children="Save"
        />
      )}
    </BottomDrawerModal>
  );
};
